import { useState, useEffect } from 'react';
import { RefreshCw, Satellite } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { getCelestialData } from '@/services/celestial-service';
import { useWeatherStore } from '@/stores/weather-store';
import { useActivePropertyStore } from '@/stores/active-property-store';
import { Card } from '@/components/common/Card';
import { ConditionMeter } from '@/components/common/ConditionMeter';
import { CONDITION_RATING_DISPLAY } from '@/components/common/conditionRatingDisplay';
import { ConditionSparkline } from '@/components/common/ConditionSparkline';
import { MoonPhaseDisplay } from '@/components/conditions/MoonPhaseDisplay';
import { PropertySelector } from '@/components/common/PropertySelector';
import { LogEntryService } from '@/services/log-entry-service';
import { WaypointService } from '@/services/waypoint-service';
import { db } from '@/services/storage/db';
import { findObservationOverlay } from '@/services/pattern-analysis-service';
import type { CurrentConditions } from '@/services/pattern-analysis-service';
import type { Property } from '@/types/property';
import type { LogEntry } from '@/types/log-entry';
import type { Waypoint } from '@/types/waypoint';
import type { QualityLevel, VariableScore } from '@/types/conditions';
import type { ObservationOverlay } from '@/types/patterns';

// ---------------------------------------------------------------------------
// Services — instantiated once at module scope (mirrors pattern-store.ts:11–12)
// ---------------------------------------------------------------------------

const logEntryService = new LogEntryService(db);
const waypointService = new WaypointService(db);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRECIP_HEADER_THRESHOLD = 10;

/**
 * LEVEL_TO_TIER — local DISPLAY-ordinal lookup off `quality.level` (Issue 3).
 *
 * The ConditionMeter tier is a lookup off the engine's `quality.level`, NEVER a
 * re-bucket of `quality.score`. The score→level ladder is the engine's concern
 * (`scoreToLevel`); the widget consumes `level` directly and maps it to the
 * 1–4 meter ordinal here.
 */
const LEVEL_TO_TIER: Record<QualityLevel, 1 | 2 | 3 | 4> = {
  poor: 1,
  fair: 2,
  good: 3,
  excellent: 4,
};

// ---------------------------------------------------------------------------
// Representative-waypoint resolution (Issue 5)
// ---------------------------------------------------------------------------

const MIN_WEATHER_BEARING_ENTRIES = 3;

interface RepresentativeWaypoint {
  id: string;
  name: string;
}

/**
 * Resolve the single representative waypoint for the property-scoped overlay,
 * reusing the selection intent from `analyzePropertyPatterns`
 * (pattern-analysis-service.ts:404–423): count weather-bearing entries per
 * waypointId, keep waypoints with `count >= 3`, and pick the MAX-count waypoint.
 * Returns null when no waypoint qualifies — the overlay then degrades to null.
 */
function resolveRepresentativeWaypoint(
  entries: LogEntry[],
  waypoints: Waypoint[],
): RepresentativeWaypoint | null {
  const countByWaypoint = new Map<string, number>();
  for (const entry of entries) {
    if (entry.weatherTemp !== undefined && entry.weatherTemp !== null) {
      countByWaypoint.set(
        entry.waypointId,
        (countByWaypoint.get(entry.waypointId) ?? 0) + 1,
      );
    }
  }

  const waypointMap = new Map(waypoints.map((w) => [w.id, w]));

  let best: RepresentativeWaypoint | null = null;
  let bestCount = 0;
  for (const [waypointId, count] of countByWaypoint) {
    if (count < MIN_WEATHER_BEARING_ENTRIES) continue;
    const waypoint = waypointMap.get(waypointId);
    if (!waypoint) continue;
    if (count > bestCount) {
      bestCount = count;
      best = { id: waypoint.id, name: waypoint.name };
    }
  }

  return best;
}

// ---------------------------------------------------------------------------
// Variable raw lookup helper
// ---------------------------------------------------------------------------

function rawForVariable(
  variables: VariableScore[] | undefined,
  variable: VariableScore['variable'],
): string | null {
  return variables?.find((v) => v.variable === variable)?.raw ?? null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ConditionsWidgetProps {
  properties: Property[];
  /** ID of the user's primary property (renamed from defaultPropertyId in M-07). */
  primaryPropertyId: string | null;
}

export function ConditionsWidget({ properties, primaryPropertyId }: ConditionsWidgetProps) {
  const [selectedId, setSelectedId] = useState<string>(() => {
    if (primaryPropertyId) {
      const found = properties.find((p) => p.id === primaryPropertyId);
      if (found) return found.id;
    }
    return properties[0]?.id ?? '';
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Property-scoped log entries + waypoints, loaded for the overlay data path.
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);

  const navigate = useNavigate();
  const { setActiveProperty } = useActivePropertyStore();

  const {
    fetchConditions,
    refreshConditions,
    getConditions,
    getConditionsQuality,
    isLoading,
    getError,
  } = useWeatherStore();

  // SAFETY: selectedId always matches a property when properties.length > 0;
  // when empty, property will be undefined and the early return below fires.
  const property = properties.find((p) => p.id === selectedId) ?? properties[0];

  // All hooks must be called unconditionally before any early return (Rules of Hooks).
  // Side-effects gate on property existence internally.
  useEffect(() => {
    if (!property) return;
    void fetchConditions(property.id, property.latitude, property.longitude);
  // Fetch when the selected property changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  // Load property-scoped log entries + waypoints for the trend-panel overlay
  // (Issue 5). Mirrors the pattern-store load (pattern-store.ts:67–70) and the
  // former waypoint-load effect — keyed on property.id.
  useEffect(() => {
    if (!property) return;
    let cancelled = false;
    const propertyId = property.id;
    void Promise.all([
      logEntryService.getEntriesByProperty(propertyId),
      waypointService.getWaypoints(propertyId),
    ])
      .then(([entries, wps]) => {
        if (cancelled) return;
        setLogEntries(entries);
        setWaypoints(wps);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[ConditionsWidget] overlay data load:', err);
        setLogEntries([]);
        setWaypoints([]);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  function handlePropertySelect(id: string) {
    setSelectedId(id);
    setActiveProperty(id);
  }

  if (properties.length === 0) {
    return (
      <div
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
        data-testid="conditions-widget-sensor-offline"
      >
        <p className="field-label">CONDITIONS</p>
        <p className="text-body-sm text-[var(--color-text-muted)]">
          Property required for environmental data.
        </p>
        <button
          type="button"
          onClick={() => navigate('/land/properties/new', { state: { fromOnboarding: true } })}
          className="self-start px-4 py-2 rounded-lg border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors"
        >
          Add Property
        </button>
      </div>
    );
  }

  const now = new Date();
  const celestial = getCelestialData(now, property.latitude, property.longitude);

  const moonRiseISO = celestial.moon.rise?.toISOString() ?? null;
  const moonSetISO = celestial.moon.set?.toISOString() ?? null;

  const weather = getConditions(property.id);
  const quality = getConditionsQuality(property.id);
  const weatherLoading = isLoading(property.id);
  const weatherError = getError(property.id);

  // ── Collapsed readout values (built from live weather; the engine variables
  //    do not reliably carry temperature in the store path — see Assumptions). ──
  const tempReadout = weather ? `${Math.round(weather.temp)}°F` : null;
  const windReadout = weather
    ? `${weather.windDirection} ${Math.round(weather.windSpeed)} mph`
    : null;
  const precipReadout =
    weather?.precipitationProbability !== undefined &&
    weather.precipitationProbability > PRECIP_HEADER_THRESHOLD
      ? `${weather.precipitationProbability}%`
      : null;

  const readoutParts = [tempReadout, windReadout, precipReadout].filter(
    (p): p is string => p !== null,
  );
  const readout = readoutParts.length > 0 ? readoutParts.join(' · ') : '—';

  // ── Rating tier word + meter tier (Issue 3 — lookup off level, NOT score). ──
  const ratingDisplay = quality ? CONDITION_RATING_DISPLAY[quality.level] : null;
  const tierWord = ratingDisplay?.word ?? (weatherLoading ? '…' : '—');
  const meterTier = quality ? LEVEL_TO_TIER[quality.level] : null;
  const meterToken = ratingDisplay?.token ?? null;

  // ── 2×2 grid raw values (built from live weather; crepuscular from the
  //    engine variable). ──
  const tempCellValue = weather ? `${Math.round(weather.temp)}°F` : '--';
  const windCellValue = weather
    ? `${weather.windDirection} ${Math.round(weather.windSpeed)} mph`
    : '--';
  const pressureCellValue = weather ? `${weather.pressure.toFixed(2)} inHg` : '--';
  const crepuscularCellValue = rawForVariable(quality?.variables, 'crepuscular') ?? '--';

  // ── Trend-panel overlay data path (Issue 5). ──
  const favorabilityByPeriod = quality?.favorabilityByPeriod ?? [];
  let overlay: ObservationOverlay | null = null;
  if (weather) {
    const repWaypoint = resolveRepresentativeWaypoint(logEntries, waypoints);
    if (repWaypoint) {
      const currentConditions: CurrentConditions = {
        temp: weather.temp,
        pressureTrend: weather.pressureTrend ?? 'steady',
        now: new Date(),
      };
      overlay = findObservationOverlay(
        logEntries,
        repWaypoint.id,
        repWaypoint.name,
        currentConditions,
      );
    }
  }

  function handleRefresh() {
    void refreshConditions(property.id, property.latitude, property.longitude);
  }

  return (
    <Card
      data-testid="conditions-widget"
      className={isExpanded ? 'overflow-visible' : 'overflow-hidden'}
    >
      {/* ── Property selector + refresh row ─────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]"
          data-testid="conditions-property-name"
        >
          {property.name}
        </p>
        <div className="flex items-center gap-2">
          {properties.length > 1 && (
            <PropertySelector
              properties={properties}
              selectedPropertyId={selectedId}
              onSelect={handlePropertySelect}
            />
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={weatherLoading}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-ember-400)] transition-colors p-1 rounded disabled:opacity-40"
            aria-label="Refresh weather"
            data-testid="refresh-weather-button"
          >
            {weatherError && !weather ? <Satellite size={14} /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {/* ── Collapsed header ───────────────────────────────────────────── */}
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[var(--color-elevated)] transition-colors"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
        data-testid="conditions-header-toggle"
      >
        {/* ConditionMeter (tier derived from quality.level via LEVEL_TO_TIER) */}
        <div className="shrink-0">
          {meterTier !== null && meterToken !== null ? (
            <ConditionMeter tier={meterTier} colorToken={meterToken} />
          ) : (
            <ConditionMeter tier={1} colorToken={CONDITION_RATING_DISPLAY.poor.token} />
          )}
        </div>

        {/* Tier word (display rename: Prime/Good/Fair/Poor) */}
        <span
          className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-primary)] shrink-0"
          data-testid="conditions-tier-word"
        >
          {tierWord}
        </span>

        {/* Readout: temp · wind · precip% */}
        <span
          className="text-[11px] text-[var(--color-text-muted)] truncate"
          data-testid="conditions-readout"
        >
          {readout}
        </span>

        {/* Expand chevron (glyph-swap via rotation) */}
        <svg
          className={`ml-auto shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-text-muted)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* ── Expanded body ───────────────────────────────────────────────── */}
      {isExpanded && (
        <div data-testid="conditions-expanded-body">
          {/* Drivers line — quality.description verbatim under a DRIVERS kicker */}
          {quality && (
            <div
              className="px-4 pb-2 flex flex-col gap-0.5"
              data-testid="conditions-drivers"
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                DRIVERS
              </p>
              <p className="text-[12px] text-[var(--color-text-primary)]">
                {quality.description}
              </p>
            </div>
          )}

          {/* 2×2 variable grid (in-file; NOT the shared cockpit grid) */}
          <div
            className="grid grid-cols-2 gap-px bg-[var(--color-border)] border-t border-[var(--color-border)]"
            data-testid="conditions-variable-grid"
          >
            {/* Temperature */}
            <div className="bg-[var(--color-surface)] px-4 py-2.5 flex flex-col gap-0.5" data-testid="grid-cell-temp">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                Temp
              </p>
              <p className="text-[14px] text-[var(--color-text-primary)]">{tempCellValue}</p>
            </div>

            {/* Wind */}
            <div className="bg-[var(--color-surface)] px-4 py-2.5 flex flex-col gap-0.5" data-testid="grid-cell-wind">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                Wind
              </p>
              <p className="text-[14px] text-[var(--color-text-primary)]">{windCellValue}</p>
            </div>

            {/* Hunt Window (crepuscular) — value = crepuscular.raw ONLY, no label sub-line (G6) */}
            <div className="bg-[var(--color-surface)] px-4 py-2.5 flex flex-col gap-0.5" data-testid="grid-cell-crepuscular">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                Hunt Window
              </p>
              <p className="text-[14px] text-[var(--color-text-primary)]">{crepuscularCellValue}</p>
            </div>

            {/* Pressure */}
            <div className="bg-[var(--color-surface)] px-4 py-2.5 flex flex-col gap-0.5" data-testid="grid-cell-pressure">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                Pressure
              </p>
              <p className="text-[14px] text-[var(--color-text-primary)]">{pressureCellValue}</p>
            </div>
          </div>

          {/* Trend panel — reflective hunt-window favorability + past observations.
              (a) empty favorability → omit the panel entirely. */}
          {favorabilityByPeriod.length > 0 && (
            <div
              className="px-4 py-3 flex flex-col gap-2 border-t border-[var(--color-border)]"
              data-testid="conditions-trend-panel"
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                Hunt window · today
              </p>
              <ConditionSparkline
                points={favorabilityByPeriod}
                sightings={overlay?.observations}
                now={new Date()}
              />
              {overlay ? (
                <p
                  className="text-[11px] text-[var(--color-text-muted)]"
                  data-testid="conditions-trend-caption"
                >
                  On similar days at {overlay.waypointName}, you observed deer on{' '}
                  {overlay.entriesWithObservation} of {overlay.totalSimilarEntries} sits.
                </p>
              ) : (
                <p
                  className="text-[11px] text-[var(--color-text-muted)]"
                  data-testid="conditions-trend-caption"
                >
                  Favorability across today&apos;s legal-light window, from the conditions
                  you recorded.
                </p>
              )}
            </div>
          )}

          {/* Weather unavailable / skeleton states */}
          {weatherLoading && !weather && (
            <div
              className="px-4 py-3 flex flex-col gap-1.5 animate-pulse border-t border-[var(--color-border)]"
              data-testid="weather-skeleton"
            >
              <div className="h-3 bg-[var(--color-border)] rounded w-28" />
              <div className="h-3 bg-[var(--color-border)] rounded w-20" />
            </div>
          )}

          {!weatherLoading && !weather && (
            <p
              className="px-4 py-3 text-[12px] text-[var(--color-text-muted)] border-t border-[var(--color-border)]"
              data-testid="weather-unavailable"
            >
              {weatherError ?? 'Weather unavailable — no connection'}
            </p>
          )}

          {/* Footer: moon phase + 8-day forecast link */}
          <div
            className="px-4 py-2.5 flex items-center justify-between gap-2 border-t border-[var(--color-border)]"
            data-testid="conditions-footer"
          >
            <div className="flex items-center gap-2" data-testid="moon-phase-line">
              <MoonPhaseDisplay
                phase={celestial.moon.phase}
                illumination={celestial.moon.illumination}
                rise={moonRiseISO}
                set={moonSetISO}
                compact
              />
            </div>
            <Link
              to={`/forecast?propertyId=${property.id}`}
              className="text-[11px] text-[var(--color-ember-400)] hover:text-[var(--color-ember-300)] transition-colors"
              data-testid="forecast-link"
            >
              8-Day Forecast →
            </Link>
          </div>

          {/* Disclaimer — always present when quality is available */}
          {quality && (
            <p
              className="px-4 pb-3 text-[10px] text-[var(--color-text-placeholder)] border-t border-[var(--color-border)] pt-2.5"
              data-testid="conditions-disclaimer"
            >
              {quality.disclaimer}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
